class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

function updateCartQuantity(lineIndex, newQuantity) {
  if (newQuantity < 0) return;
  
  // Disable buttons temporarily to prevent multiple clicks
  const cartItem = document.querySelector(`[data-line="${lineIndex}"]`);
  if (cartItem) {
    const buttons = cartItem.querySelectorAll('.qty-btn');
    buttons.forEach(btn => btn.disabled = true);
  }
  
  fetch('/cart/change.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      line: lineIndex,
      quantity: newQuantity
    })
  })
  .then(response => response.json())
  .then(cart => {
    // Update the specific quantity display and totals
    updateQuantityDisplay(lineIndex, newQuantity, cart);
    // Update header cart count
    updateHeaderCartCount(cart.item_count);
    
    // Re-enable buttons
    if (cartItem && newQuantity > 0) {
      const buttons = cartItem.querySelectorAll('.qty-btn');
      buttons.forEach(btn => btn.disabled = false);
    }
  })
  .catch(error => {
    console.error('Error updating cart:', error);
    // Re-enable buttons on error
    if (cartItem) {
      const buttons = cartItem.querySelectorAll('.qty-btn');
      buttons.forEach(btn => btn.disabled = false);
    }
  });
}

function updateQuantityDisplay(lineIndex, newQuantity, cart) {
  const cartItem = document.querySelector(`[data-line="${lineIndex}"]`);
  
  if (newQuantity === 0) {
    // Remove the entire item if quantity is 0
    if (cartItem) cartItem.remove();
  } else {
    // Update the quantity display
    const qtyDisplay = cartItem?.querySelector('.qty-display');
    if (qtyDisplay) {
      qtyDisplay.textContent = newQuantity;
    }
    
    // Update the onclick handlers with new quantities
    const minusBtn = cartItem?.querySelector('.qty-btn:first-child');
    const plusBtn = cartItem?.querySelector('.qty-btn:last-child');
    
    if (minusBtn) {
      minusBtn.setAttribute('onclick', `updateCartQuantity(${lineIndex}, ${newQuantity - 1})`);
    }
    if (plusBtn) {
      plusBtn.setAttribute('onclick', `updateCartQuantity(${lineIndex}, ${newQuantity + 1})`);
    }
  }
  
  // Update cart totals
  const cartTotal = document.querySelector('[data-cart-total]');
  if (cartTotal && cart.items_subtotal_price) {
    cartTotal.textContent = '$' + (cart.items_subtotal_price / 100).toFixed(2);
  }
  
  const estimatedTotal = document.querySelector('.cart-drawer__footer .totals__total-value');
  if (estimatedTotal && cart.items_subtotal_price) {
    estimatedTotal.textContent = '$' + (cart.items_subtotal_price / 100).toFixed(2) + ' USD';
  }
}

function updateHeaderCartCount(itemCount) {
  const cartCountSelectors = [
    '.cart-count-bubble',
    '[data-cart-count]',
    '.cart-count',
    '.header__icon--cart .cart-count-bubble',
    '.cart-link [data-cart-count]',
    '.cart-icon-bubble'
  ];
  
  cartCountSelectors.forEach(selector => {
    const cartCountElement = document.querySelector(selector);
    if (cartCountElement) {
      cartCountElement.textContent = itemCount;
      if (itemCount === 0) {
        cartCountElement.style.display = 'none';
      } else {
        cartCountElement.style.display = '';
      }
    }
  });
}

function removeCartItem(lineIndex) {
  // Disable the remove button to prevent multiple clicks
  const removeBtn = document.querySelector(`[data-line="${lineIndex}"] .remove-btn`);
  if (removeBtn) removeBtn.disabled = true;
  
  fetch('/cart/change.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      line: lineIndex,
      quantity: 0
    })
  })
  .then(response => response.json())
  .then(cart => {
    // Remove the item from DOM
    const cartItem = document.querySelector(`[data-line="${lineIndex}"]`);
    if (cartItem) cartItem.remove();
    
    // Update totals without reloading
    updateCartTotals(cart);
    updateHeaderCartCount(cart.item_count);
    
    // Only show empty cart message if truly empty, don't reload
    if (cart.item_count === 0) {
      showEmptyCartMessage();
    }
  })
  .catch(error => {
    console.error('Error removing item:', error);
    // Re-enable button on error
    if (removeBtn) removeBtn.disabled = false;
  });
}

function updateCartTotals(cart) {
  // Update the estimated total
  const estimatedTotal = document.querySelector('.cart-drawer__footer .totals__total-value');
  if (estimatedTotal) {
    if (cart.items_subtotal_price === 0) {
      estimatedTotal.textContent = '$0.00 USD';
    } else {
      estimatedTotal.textContent = '$' + (cart.items_subtotal_price / 100).toFixed(2) + ' USD';
    }
  }
  
  // Update any other total displays
  const otherTotals = document.querySelectorAll('[data-cart-total], .cart-total');
  otherTotals.forEach(total => {
    if (cart.items_subtotal_price === 0) {
      total.textContent = '$0.00 USD';
    } else {
      total.textContent = '$' + (cart.items_subtotal_price / 100).toFixed(2) + ' USD';
    }
  });
}

function showEmptyCartMessage() {
  // Hide checkout button
  const checkoutBtn = document.querySelector('#CartDrawer-Checkout, .cart-drawer__checkout');
  if (checkoutBtn) {
    checkoutBtn.style.display = 'none';
  }
  
  // Show empty message
  const cartItems = document.querySelector('.cart-drawer__collection');
  if (cartItems) {
    cartItems.innerHTML = '<div style="text-align: center; padding: 40px 20px;"><p>Your cart is empty</p></div>';
  }
}


